import { useHomeLang } from "./homeLang";

// The homepage's copy, kept apart from the layout, in Italian and English
// (`CONTENT`), with the values the same in both exported on their own.
// Sections read their language's with useContent().

// One advisor’s day. Each need drives both the thoughts section and the studio
// configurator's template chips (`studio` marks the ones offered there). Its
// `id` names it the same in every language.
const NEEDS = [
  {
    time: "07:45",
    quote: "Ho cinquecento clienti. Come posso essere presente per tutti?",
    // The book the thought speaks of, which its scene sends the update to.
    clients: 500,
    id: "update",
    template: "Aggiornamento clienti",
    sceneTitle: "Il punto del mese sul portafoglio",
    answer: "Registri un solo messaggio video e raggiungi tutti con la tua voce e il tuo volto.",
    studio: true,
  },
  {
    time: "10:20",
    quote: "Come mi distinguo?",
    id: "intro",
    template: "Presentazione personale",
    sceneTitle: "Piacere, sono il tuo consulente",
    answer: "Con A.V.R.A.H., il tuo studio digitale premium: una presenza discreta e costante accanto a ogni cliente, anche quando il tuo portafoglio cresce.",
    studio: true,
  },
  {
    time: "13:05",
    quote: "I figli dei miei clienti sanno chi sono?",
    id: "family",
    template: "Passaggio generazionale",
    sceneTitle: "Per tutta la famiglia",
    studioChip: "Presenta il tuo team",
    studioTitle: "Il team che ti segue",
    answer: "Per farti conoscere dai figli dei tuoi clienti, scegli il linguaggio che sentono più vicino: il video.",
    studio: true,
  },
  {
    time: "16:40",
    quote: "Come trovo il tempo per tutti?",
    id: "quick",
    template: "Registrazione rapida",
    sceneTitle: "Il documento, spiegato in due minuti",
    answer: "Le stesse domande, una sola risposta in video. Più tempo per tutto ciò che richiede la tua attenzione.",
    studio: false,
  },
  {
    time: "19:30",
    quote: "Come anticipo le domande sui mercati?",
    id: "markets",
    template: "Commento ai mercati",
    sceneTitle: "Cosa sta succedendo sui mercati",
    answer: "Il tuo commento arriva prima delle domande, rassicura e rafforza la fiducia.",
    studio: true,
  },
];

// The brand colours the studio offers, A.V.R.A.H.'s own amber first. As in
// the product, the brand colour fills the lower third's title band.
const BRAND_COLORS = [
  { hex: "#e0a33e", name: "ambra" },
  { hex: "#2f5d8c", name: "blu" },
  { hex: "#2e7d5b", name: "verde" },
  { hex: "#8e2f45", name: "bordeaux" },
  { hex: "#2b2f36", name: "grafite" },
];

const ROLES = ["Consulente finanziario", "Consulente assicurativo", "Private banker", "Wealth advisor"];

const DEMO_TEAM = [
  { name: "Caterina Marinelli", role: "analista" },
  { name: "Diego Cecchini", role: "assistente clienti" },
  { name: "Laura Franchini", role: "consulente fiscale" },
];

// The warning every studio video carries, as the product shows it.
const DISCLAIMER = "Messaggio a scopo informativo, non costituisce consulenza personalizzata né sollecitazione all’investimento. I rendimenti passati non sono indicativi di quelli futuri.";

// The showreel, made with A.V.R.A.H.
export const SHOWREEL = {
  duration: "2:33",
  src: "/home/avrah-intro.mp4",
  poster: "/home/avrah-intro.jpg",
};

const TESTIMONIALS = [
  {
    quote: "Il tempo è prezioso.",
    who: "Marcello B., Private Banker Banca Mediolanum",
    duration: "0:20",
    src: "/home/testimonial-marcello.mp4",
    poster: "/home/testimonial-marcello.jpg",
  },
  {
    quote: "Arrivo prima delle domande.",
    who: "Massimo M., Private Banker BNP Paribas",
    duration: "0:20",
    src: "/home/testimonial-massimo.mp4",
    poster: "/home/testimonial-massimo.jpg",
  },
  {
    quote: "Lo strumento è digitale. La fiducia resta umana.",
    who: "Luca L., Manager Banco Mediolanum",
    duration: "0:24",
    src: "/home/testimonial-luca.mp4",
    poster: "/home/testimonial-luca.jpg",
  },
];

const PRESS = [
  {
    outlet: "Wall Street Italia",
    when: "ottobre 2026",
    title: "Jacopo Mascitelli, CTO e founder di AVRAH: «più cresce l’AI più aumenta il valore della relazione umana»",
    href: "https://www.wallstreetitalia.com/wall-street-italia-il-sommario-del-numero-di-ottobre-2026/#advisory",
  },
  {
    outlet: "Wall Street Italia",
    when: "24 luglio 2026",
    title: "Home SetV® alla conquista dell’Europa: obiettivo 250.000 clienti in tre anni",
    href: "https://www.wallstreetitalia.com/home-setv-alla-conquista-delleuropa-obiettivo-250-000-clienti-in-tre-anni/",
  },
];

const FAQ = [
  ["Mi serve un’attrezzatura speciale?", "No. A.V.R.A.H. funziona con la webcam del tuo computer. Se vuoi un ambiente immersivo, aggiungi un kit di luci e green screen."],
  ["Funziona con il Teams della mia azienda?", "Sì. A.V.R.A.H. funziona con tutte le app per videoconferenze: Teams, Zoom, Meet e le altre."],
  ["Dove finiscono i miei video?", "Sul tuo computer. A.V.R.A.H. non è un’app in cloud: lavora in locale."],
  ["E la compliance della mia rete?", "Inserisci le didascalie e le avvertenze richieste dalla tua rete. Restano visibili per tutto il video."],
  ["Posso pubblicare sui miei canali?", "Sì. Invii il video ai tuoi canali direttamente da A.V.R.A.H.."],
  ["Posso provarlo prima di pagare?", "Sì. Provi A.V.R.A.H. Premium per 14 giorni, senza addebito."],
  ["Posso disdire quando voglio?", "Sì. Disdici dal tuo account, entro il periodo di validità dell’abbonamento."],
];

const NETWORK_POINTS = [
  ["Modelli della Rete", "Scene e grafica decise dalla sede, uguali per tutti."],
  ["Compliance by design", "Avvertenze e presidi della Rete dentro ogni video."],
  ["Local-first, europeo", "Documenti e registrazioni restano sul computer del consulente."],
];

// How long the free trial lasts, in days.
export const TRIAL_DAYS = 14;

export const SALES_EMAIL = "info@homesetv.com";

const IT = { NEEDS, BRAND_COLORS, ROLES, DEMO_TEAM, DISCLAIMER, TESTIMONIALS, PRESS, FAQ, NETWORK_POINTS };

// The English copy. Lists whose items also carry values the same in every
// language (ids, times, media, links) take those from the Italian, item by
// item, and only their words from here.
const translated = (items, words) => {
  if (words.length !== items.length) throw new Error(`content.js: ${words.length} English items for ${items.length} Italian ones`);
  return items.map((item, i) => ({ ...item, ...words[i] }));
};

const EN = {
  NEEDS: translated(NEEDS, [
    {
      quote: "I have five hundred clients. How can I be there for all of them?",
      template: "Client update",
      sceneTitle: "This month’s portfolio update",
      answer: "Record a single video message and reach every client with your own voice and face.",
    },
    {
      quote: "How do I stand out?",
      template: "Personal introduction",
      sceneTitle: "Nice to meet you, I’m your advisor",
      answer: "With A.V.R.A.H., your premium digital studio: a discreet, constant presence beside every client, even as your book grows.",
    },
    {
      quote: "Do my clients’ children know who I am?",
      template: "Next generation",
      sceneTitle: "For the whole family",
      studioChip: "Introduce your team",
      studioTitle: "The team looking after you",
      answer: "To introduce yourself to your clients’ children, speak the language they feel closest to: video.",
    },
    {
      quote: "How do I find time for everyone?",
      template: "Quick recording",
      sceneTitle: "The document, explained in two minutes",
      answer: "The same questions, one video answer. More time for everything that needs your attention.",
    },
    {
      quote: "How do I get ahead of questions about the markets?",
      template: "Market commentary",
      sceneTitle: "What’s happening in the markets",
      answer: "Your commentary arrives before the questions do. It reassures, and it strengthens trust.",
    },
  ]),
  BRAND_COLORS: translated(BRAND_COLORS, [
    { name: "amber" },
    { name: "blue" },
    { name: "green" },
    { name: "burgundy" },
    { name: "graphite" },
  ]),
  ROLES: ["Financial advisor", "Insurance advisor", "Private banker", "Wealth advisor"],
  DEMO_TEAM: translated(DEMO_TEAM, [
    { role: "analyst" },
    { role: "client services" },
    { role: "tax advisor" },
  ]),
  DISCLAIMER: "For information purposes only. This message does not constitute personalized advice or a solicitation to invest. Past performance is not indicative of future results.",
  TESTIMONIALS: translated(TESTIMONIALS, [
    { quote: "Time is precious.", who: "Marcello B., Private Banker at Banca Mediolanum" },
    { quote: "I get there before the questions do.", who: "Massimo M., Private Banker at BNP Paribas" },
    { quote: "The tool is digital. The trust stays human.", who: "Luca L., Manager at Banco Mediolanum" },
  ]),
  PRESS: translated(PRESS, [
    {
      when: "October 2026",
      title: "Jacopo Mascitelli, CTO and founder of AVRAH: “The more AI grows, the more valuable the human relationship becomes”",
    },
    {
      when: "24 July 2026",
      title: "Home SetV® sets out to conquer Europe: a target of 250,000 clients in three years",
    },
  ]),
  FAQ: [
    ["Do I need special equipment?", "No. A.V.R.A.H. works with your computer’s webcam. For an immersive setting, add a lighting and green screen kit."],
    ["Does it work with my company’s Teams?", "Yes. A.V.R.A.H. works with every video conferencing app: Teams, Zoom, Meet and the rest."],
    ["Where do my videos end up?", "On your computer. A.V.R.A.H. isn’t a cloud app: it works locally."],
    ["What about my network’s compliance?", "Add the captions and disclaimers your network requires. They stay on screen for the whole video."],
    ["Can I publish on my own channels?", "Yes. You send the video to your channels straight from A.V.R.A.H."],
    ["Can I try it before I pay?", "Yes. Try A.V.R.A.H. Premium for 14 days, with no charge."],
    ["Can I cancel at any time?", "Yes. Cancel from your account, before your current subscription period ends."],
  ],
  NETWORK_POINTS: [
    ["Network templates", "Scenes and graphics set by head office, the same for everyone."],
    ["Compliance by design", "Your network’s disclaimers and safeguards, built into every video."],
    ["Local-first, European", "Documents and recordings stay on the advisor’s computer."],
  ],
};

export const CONTENT = { it: IT, en: EN };

// The copy in the page's language.
export function useContent() {
  return CONTENT[useHomeLang()];
}
